import { type InfiniteData, useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { useStoreScope } from "@/hooks/use-store-context";
import { api } from "../runtime";
import type { Conversation, ConversationListItem, Message, Page } from "../types";
import { qk } from "./keys";

export const CONVERSATION_FILTERS = ["all", "needs_reply", "rogue_working", "human", "failed"] as const;
export type ConversationFilter = (typeof CONVERSATION_FILTERS)[number];

export interface ConversationListFilters {
  filter?: ConversationFilter;
  q?: string;
  /** Include lost and opted-out sellers (hidden from the queue by default). */
  includeClosed?: boolean;
}

/** [planned] GET /v1/conversations */
export function useConversations(filters: ConversationListFilters) {
  const scope = useStoreScope();
  return useInfiniteQuery({
    queryKey: qk.conversations.list(scope, filters),
    queryFn: ({ pageParam, signal }) =>
      api.get<Page<ConversationListItem>>("/v1/conversations", {
        query: {
          filter: filters.filter !== "all" ? filters.filter : undefined,
          q: filters.q,
          includeClosed: filters.includeClosed ? "true" : undefined,
          cursor: pageParam ?? undefined,
          limit: 40,
        },
        signal,
      }),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor,
    retry: false,
  });
}

export function useConversation(id: string | null | undefined) {
  return useQuery({
    queryKey: qk.conversations.detail(id ?? "none"),
    queryFn: async () => (await api.get<{ conversation: Conversation }>(`/v1/conversations/${id}`)).conversation,
    enabled: Boolean(id),
    staleTime: 5_000,
  });
}

export const MESSAGE_PAGE_SIZE = 50;

/**
 * Messages, newest page first. Each page is chronological; older pages are loaded on demand
 * with ?before=<oldest id>. Realtime events patch this cache (see use-sse).
 */
export function useMessages(conversationId: string | null | undefined) {
  return useInfiniteQuery({
    queryKey: qk.conversations.messages(conversationId ?? "none"),
    queryFn: async ({ pageParam, signal }) =>
      (
        await api.get<{ items: Message[] }>(`/v1/conversations/${conversationId}/messages`, {
          query: { limit: MESSAGE_PAGE_SIZE, before: pageParam ?? undefined },
          signal,
        })
      ).items,
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) => (lastPage.length === MESSAGE_PAGE_SIZE ? (lastPage[0]?.id ?? null) : null),
    enabled: Boolean(conversationId),
    staleTime: 30_000,
  });
}

export type MessagesData = InfiniteData<Message[], unknown>;

/** Flattens the infinite cache into chronological order (oldest first). */
export function flattenMessages(data: MessagesData | undefined): Message[] {
  if (!data) return [];
  return [...data.pages].reverse().flat();
}

/**
 * A photo in a conversation: a short-lived URL the backend makes for it (provider media needs the
 * provider's credentials, so it's copied to our storage first). Refreshed before the URL expires.
 */
export function useMessageMedia(messageId: string, index: number, enabled = true) {
  return useQuery({
    queryKey: qk.conversations.media(messageId, index),
    queryFn: () => api.get<{ url: string; contentType: string }>(`/v1/messages/${messageId}/media/${index}`),
    enabled,
    staleTime: 10 * 60_000,
    retry: false,
  });
}
